import axiosInstance from "../axios";
import { getLoginToken } from "../storage";
import { BaseUrl, COLLECTIONS, DOMAINURL, EMP_ROLES } from "../config";
import {
  collection,
  query,
  where,
  getDocs,
  documentId,
  serverTimestamp,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  getDoc,
  limit,
  onSnapshot,
  deleteField,
} from "firebase/firestore";
import { firebase } from "../firebase-config";
import { fetchSignInMethodsForEmail, getAuth } from "firebase/auth";
import EmailTemplate from "../../app/auth/EmailTemplate";
import moment from "moment";
import { updateStripeQuantity, phoneForFirebaseAuth } from "./employee";
import {
  nationalPhoneForStorage,
  normalizePhoneFields,
  validateAndNormalizePhone,
} from "../../utils/phoneValidation";
import {
  buildClientPatchForPortalUserUpdate,
  commitPortalUserAndClientUpdate,
  getClientContactByEmail,
  isClientPortalRole,
  PORTAL_EMAIL_CHANGE_BLOCKED_MESSAGE,
  portalContactHasActiveInvite,
} from "../../utils/clientPortalSync";
import { computeDefaultWebPortalAccess } from "../planWebPortalDefaults";
import {
  deriveWebPortalAccessFromFeatures,
  normalizePlan,
  normalizePlanCollection,
} from "../accessControl";

const defaultHeaders = {
  "Content-Type": "application/json",
};

let userCollection = COLLECTIONS.USERS;
const APP_CONFIG_CACHE_TTL_MS = 30 * 60 * 1000;
const CUSTOMER_BALANCE_CACHE_TTL_MS = 60 * 1000;
let appConfigCache = null;
let appConfigCacheTs = 0;
let appConfigInflight = null;
const customerBalanceCache = new Map();
const customerBalanceInflight = new Map();

const APP_CONFIG_SESSION_KEY = "pt_cms_app_config_v1";
const PROMO_CODES_SESSION_KEY = "pt_cms_promo_codes_v1";

function readSessionCache(key, maxAgeMs) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (Date.now() - parsed.ts > maxAgeMs)) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeSessionCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch (e) {
    if (e?.name === "QuotaExceededError") {
      sessionStorage.removeItem(key);
    }
  }
}

export async function getPlanAccessByStripeProductId(subscriptionProdId) {
  if (!subscriptionProdId) return null;

  const fetchByField = async (collectionName, fieldName) => {
    const ref = collection(firebase, collectionName);
    const q = query(ref, where(fieldName, "==", subscriptionProdId), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  };

  try {
    const plan =
      (await fetchByField("plansAccess", "stripeProductId")) ||
      (await fetchByField("planAccess", "stripeProductId")) ||
      null;
    return plan ? normalizePlan(plan) : null;
  } catch (_e) {
    return null;
  }
}

export async function saveCancellationReasonOnly(customerId, formData = {}) {
  try {
    const userRef = collection(firebase, COLLECTIONS.USERS);
    const q = query(userRef, where("customer", "==", customerId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const userDocRef = userDoc.ref;
      const userData = userDoc.data() || {};
      const cancelReason = {
        feedBack: formData?.feedBack || "",
        selectedOption: formData?.selectedOption || "",
        cancelAt: serverTimestamp(),
      };
      const billingState = userData?.billingState || {};
      await updateDoc(userDocRef, {
        ...cancelReason,
        billingAccessRestricted: true,
        billingCancellation: {
          policyType: "freemium_cancel",
          canceledAt: serverTimestamp(),
          effectivePeriodEnd: null,
        },
        billingState: {
          ...billingState,
          subscriptionStatus: "canceled",
          cancelAtPeriodEnd: false,
          updatedAt: serverTimestamp(),
        },
      });
    }

    return {
      success: true,
      message: "Cancellation reason saved.",
    };
  } catch (error) {
    console.error("Error saving cancellation reason:", error);
    return {
      success: false,
      error: error.message || error,
    };
  }
}

/**
 * Writes only `webPortalAccess` (+ `updatedAt`) on each `plansAccess` document.
 * Fetches existing docs, computes defaults from current fields; does not modify `features`, `limits`, `name`, etc.
 * With `overwrite: false` (default), skips any doc that already has a non-empty `webPortalAccess`.
 *
 * @param {Object} [options]
 * @param {boolean} [options.overwrite=false] — Replace non-empty existing `webPortalAccess`
 * @param {boolean} [options.dryRun=false] — Only return what would be written
 * @param {Record<string, Record<string, boolean>>} [options.overridesByPlanId] — e.g. { trial_plan: { fullAccess: true } }
 * @returns {Promise<{ updated: number, skipped: number, docs: Array<{ id: string, planId: string, webPortalAccess: object }> }>}
 */
export async function syncWebPortalAccessForPlans(options = {}) {
  const {
    overwrite = false,
    dryRun = false,
    overridesByPlanId = {},
  } = options;

  const ref = collection(firebase, "plansAccess");
  const snapshot = await getDocs(ref);
  const results = { updated: 0, skipped: 0, docs: [] };

  for (const snap of snapshot.docs) {
    const data = snap.data();
    const planId = data.planId || snap.id;
    const hasExisting =
      data.webPortalAccess &&
      typeof data.webPortalAccess === "object" &&
      !Array.isArray(data.webPortalAccess) &&
      Object.keys(data.webPortalAccess).length > 0;

    if (!overwrite && hasExisting) {
      results.skipped += 1;
      continue;
    }

    const computed = computeDefaultWebPortalAccess(data);
    const override =
      overridesByPlanId[planId] || overridesByPlanId[data.planId] || null;
    const webPortalAccess =
      override && typeof override === "object" && !Array.isArray(override)
        ? { ...computed, ...override }
        : computed;
    const normalizedAccess = normalizePlan({
      features: data?.features || {},
      webPortalAccess,
    }).webPortalAccess;

    if (dryRun) {
      results.docs.push({
        id: snap.id,
        planId: data.planId || "",
        webPortalAccess,
      });
      continue;
    }

    await updateDoc(doc(firebase, "plansAccess", snap.id), {
      webPortalAccess: normalizedAccess,
      updatedAt: serverTimestamp(),
    });
    results.updated += 1;
  }

  return results;
}

export async function getAllPlansAccess() {
  try {
    const ref = collection(firebase, "plansAccess");
    const snapshot = await getDocs(ref);

    if (snapshot.empty) return [];

    const plans = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return normalizePlanCollection(plans);
  } catch (error) {
    console.error("Error fetching plansAccess:", error);
    return [];
  }
}

export function subscribePlanAccessByStripeProductId(subscriptionProdId, onData, onError) {
  if (!subscriptionProdId || typeof onData !== "function") {
    return () => {};
  }

  const unsubscribers = [];
  let latestPlanKey = null;

  const handleSnapshot = (querySnapshot) => {
    if (querySnapshot.empty) return;
    const docSnap = querySnapshot.docs[0];
    const plan = { id: docSnap.id, ...docSnap.data() };
    const nextPlanKey = JSON.stringify(plan);
    if (nextPlanKey === latestPlanKey) {
      return;
    }
    latestPlanKey = nextPlanKey;
    onData(normalizePlan(plan));
  };

  const handleError = (error) => {
    if (typeof onError === "function") onError(error);
  };

  const sources = [
    { collectionName: "planAccess", fieldName: "price.stripeProductId" },
    { collectionName: "planAccess", fieldName: "stripeProductId" },
    { collectionName: "plansAccess", fieldName: "price.stripeProductId" },
    { collectionName: "plansAccess", fieldName: "stripeProductId" },
  ];

  sources.forEach(({ collectionName, fieldName }) => {
    const ref = collection(firebase, collectionName);
    const q = query(ref, where(fieldName, "==", subscriptionProdId), limit(1));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        handleSnapshot(snap);
        if (!latestPlanKey && snap.empty) {
          // keep waiting for live updates
        }
      },
      handleError
    );
    unsubscribers.push(unsubscribe);
  });

  return () => {
    unsubscribers.forEach((fn) => {
      try {
        fn();
      } catch (_e) {
        // ignore unsubscribe errors
      }
    });
  };
}

/**
 * Unique validate user by email and phone number
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function validateUniqueUser(formData, onSuccess, _onError) {
  let responseObj = {};
  let isValid = true;
  const auth = getAuth();

  // Check for email
  if (formData.email && formData.email !== "") {
    const userRef = collection(firebase, userCollection);
    const emailQuery = query(userRef, where("email", "==", formData.email));
    const snapshot = await getDocs(emailQuery);
  
    let emailConflict = false;
  
    snapshot.forEach((doc) => {
      const data = doc.data();
  
      const isSameUser = formData.userId && formData.userId === data.userId;
      const isDeleted = data.deleted === true;
  
      if (!isDeleted && !isSameUser) {
        emailConflict = true;
      }
    });
  
    let firebaseEmailConflict = false;
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, formData.email);
      if (signInMethods.length > 0) {
        firebaseEmailConflict = true;
      }
    } catch (err) {
      console.log("Auth email check failed:", err);
    }

    if (emailConflict || firebaseEmailConflict) {
      responseObj = { ...responseObj, email: "Email Already Registered" };
      isValid = false;
    }
  }

  if (formData.mobile && formData.mobile !== "") {
    const userRef = collection(firebase, userCollection);
    const mobileQuery = query(userRef, where("phoneNumber", "==", formData.mobile));
    const snapshotMobile = await getDocs(mobileQuery);
  
    let hasConflict = false;
  
    snapshotMobile.forEach((doc) => {
      const data = doc.data();
  
      if (data.deleted !== true && data.userId !== formData.userId) {
        hasConflict = true;
      }
    });
  
    if (hasConflict) {
      isValid = false;
      responseObj = {
        ...responseObj,
        mobile: "Mobile Number Already Registered",
        phoneNumber: "Mobile Number Already Registered",
      };
    }
  }

  onSuccess({ status: isValid, data: responseObj });
}

/**
 * Get list of client users (Not used after restructured)
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getClientList(formData, onSuccess, onError) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  try {
    const res = await axiosInstance.get(`${BaseUrl}/clients`, {
      params: { ...formData },
      headers: { ...defaultHeaders, ...authHeaders },
    });
    onSuccess && onSuccess(res);
  } catch (error) {
    onError && onError(error);
  }
}

/**
 * Get list of users (Not used after restructured)
 * @param {*} formData
 * @param {*} onSuccess
 * @param {*} onError
 */
export async function getUserListBasedOnIds(userIDs, onSuccess, onError) {
  const chunk = (arr, chunkSize) => {
    const R = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      R.push(arr.slice(i, i + chunkSize));
    }
    return R;
  };

  const usersNew = [];
  try {
    const userChunks = chunk(userIDs, 10);
    for (const chunkIds of userChunks) {
      const colRef = collection(firebase, userCollection);
      const q = query(colRef, where(documentId(), "in", chunkIds));
      const snap = await getDocs(q);

      snap.forEach((doc) => {
        const userData = { ...doc.data(), id: doc.id };
        usersNew.push(userData);
      });
    }
    onSuccess && onSuccess(usersNew);
  } catch (error) {
    console.error("Error getting documents: ", error);
    onError && onError(error);
  }
}

async function createUsers(formData) {
  const currentUserId = getAuth().currentUser.uid;

  try {
    const userDocRef = doc(firebase, COLLECTIONS.USERS, formData?.userId);

    await setDoc(userDocRef, {
      ...formData,
      createdBy: currentUserId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const responseItem = {
      ...formData,
      id: formData?.userId,
    };
    return { status: true, data: responseItem };
  } catch (error) {
    return { status: false, error: error.message || error };
  }
}

export async function updateStripeCustomerName(customerId, fullName) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  await axiosInstance.post(`${BaseUrl}stripeProxyUpdateCustomer`, { customerId, name: fullName }, { headers: { ...defaultHeaders, ...authHeaders } });
}

const areArraysEqual = (a, b) => {
  if (!a || !b) return false;
  if (a?.length !== b?.length) return false;
  return [...a]?.sort()?.every((val, index) => val === [...b]?.sort()[index]);
};


/** Firestore rejects `undefined`; passwords must not be stored on user docs. */
function sanitizeFirestoreUserUpdate(data = {}) {
  const { password, confirmPassword, ...rest } = data || {};
  return Object.fromEntries(
    Object.entries(rest).filter(([, value]) => value !== undefined)
  );
}

export function getUserSaveErrorMessage(error) {
  const raw = String(error?.message || error || "").trim();
  if (!raw) return "Unable to save user changes. Please try again.";
  if (/unsupported field value:\s*undefined/i.test(raw)) {
    return "Unable to save user changes. Please try again or contact support if this continues.";
  }
  if (/writebatch\.set\(\)|invalid data/i.test(raw)) {
    return "Unable to save user changes. Please review the form and try again.";
  }
  if (/permission-denied|missing or insufficient permissions/i.test(raw)) {
    return "You do not have permission to update this user.";
  }
  return raw;
}

export async function saveOrUpdateUserData(formData, id) {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;

  try {
    if (!currentUserId) {
      throw new Error("No authenticated user found");
    }

    let newData = null;

    if (id) {
      const userDocRef = doc(firebase, COLLECTIONS.USERS, id);

      const existingUserSnap = await getDoc(userDocRef);
      const existingUser = existingUserSnap.data();
      const isSuperUser = existingUser?.role === "superUser";

      if (
        !isSuperUser &&
        formData?.phoneNumber &&
        String(formData.phoneNumber).trim() !== ""
      ) {
        let companyCountryForPhone =
          formData.country || existingUser?.country;
        const ownerCompanyId =
          formData.companyId || existingUser?.companyId || null;
        if (ownerCompanyId) {
          try {
            const companySnap = await getDoc(
              doc(firebase, COLLECTIONS.USERS, String(ownerCompanyId))
            );
            if (companySnap.exists()) {
              companyCountryForPhone =
                companySnap.data()?.country || companyCountryForPhone;
            }
          } catch (companyCountryError) {
            console.error("Failed to resolve company country:", companyCountryError);
          }
        }
        const phoneCheck = validateAndNormalizePhone(
          { ...existingUser, ...formData },
          companyCountryForPhone
        );
        if (!phoneCheck.ok) {
          return {
            status: false,
            field: "phone",
            message: phoneCheck.error,
          };
        }
        formData.phoneNumber = phoneCheck.fields.phoneNumber;
        formData.country = phoneCheck.fields.country;
        formData.phoneCountryCode = phoneCheck.fields.phoneCountryCode;
      }

      const isPortalClient = isClientPortalRole(existingUser?.role);
      const linkedClientId = existingUser?.client || null;
      const portalContactEmail = String(existingUser?.email || "").trim();

      if (
        isPortalClient &&
        linkedClientId &&
        formData?.email &&
        portalContactEmail &&
        String(formData.email).trim().toLowerCase() !== portalContactEmail.toLowerCase()
      ) {
        const contact = await getClientContactByEmail(linkedClientId, portalContactEmail);
        if (portalContactHasActiveInvite(contact)) {
          return {
            status: false,
            field: "email",
            message: PORTAL_EMAIL_CHANGE_BLOCKED_MESSAGE,
          };
        }
      }

      if (formData?.phoneNumber && formData?.phoneNumber !== existingUser.phoneNumber) {
        const phoneQuery = query(
          collection(firebase, COLLECTIONS.USERS),
          where("phoneNumber", "==", formData?.phoneNumber),
          where("status", "==", true),
          limit(1)
        );
        const phoneSnapshot = await getDocs(phoneQuery);

        if (!phoneSnapshot.empty) {
          return {
            status: false,
            field: "phone",
            message: "A user with this phone number already exists.",
          };
        }
      }

      const isClientTeam = formData?.employeerole === EMP_ROLES.CLIENT_TEAM;
      const roleChanged = !areArraysEqual(existingUser?.role, formData?.role);
      const EmpRoleChanged = (existingUser?.employeerole ?? null) !== (formData?.employeerole ?? null);
      const shouldGeneratePassword = roleChanged || (isClientTeam && EmpRoleChanged)
      const role = formData?.role;
  
      if (shouldGeneratePassword && (!role || (Array.isArray(role) ? !role.includes("companyUser") : role !== "companyUser"))) {
        const authHeadersRole = { Authorization: `Bearer ${getLoginToken()}` };
        Promise.resolve()
          .then(async () => {
            try {
              await axiosInstance.post(
                `${BaseUrl}sendManagedUserPasswordResetLink`,
                {
                  email: formData?.email,
                  richAppInvite: isClientTeam,
                  inviteFirstName: formData?.firstName,
                  contactName: "",
                  companyName: "",
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    ...authHeadersRole,
                  },
                }
              );
            } catch (err) {
              console.error("role change secure link error:", err);
            }
          })
          .catch((err) => console.error("role change secure link error:", err));
      }

      let newEmail = null;
      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      
      if (formData?.email && existingUser?.email && existingUser?.email !== formData?.email) {
        const oldEmail = existingUser?.email;
        newEmail = formData?.email;
        const displayName = `${formData?.firstName || existingUser?.firstName} ${formData?.lastName || existingUser?.lastName}`;
        const userId = id;

      if (formData) formData.email = oldEmail;
        if (formData?.employeerole === EMP_ROLES.CLIENT_TEAM) {
          await axiosInstance.post(`${BaseUrl}employeeEmailVerifyLink`, {
            oldEmail,
            newEmail,
            displayName,
            userId,
          }, { headers: { ...defaultHeaders, ...authHeaders }});
        } else {
          await axiosInstance.post(`${BaseUrl}emailVerifyLink`, {
            oldEmail,
            newEmail,
            displayName,
            userId,
          }, { headers: { ...defaultHeaders, ...authHeaders }});
        }
          // await axiosInstance.post(`${BaseUrl}updateUserAuthEmail`,
          // {
          //   userId: id,
          //   newEmail: formData?.email,
          // }, {
          //   headers: { ...defaultHeaders, ...authHeaders },
          // });
      }

      const updateData = sanitizeFirestoreUserUpdate({
        ...formData,
        ...(newEmail !== null ? { pendingEmail: newEmail } : {}),
        updatedBy: currentUserId,
        updatedAt: serverTimestamp(),
      });

      const nextFirstName = formData?.firstName ?? existingUser?.firstName;
      const nextLastName = formData?.lastName ?? existingUser?.lastName;
      const nextPhone = formData?.phoneNumber ?? existingUser?.phoneNumber;
      const nameChanged =
        String(nextFirstName || "").trim() !== String(existingUser?.firstName || "").trim() ||
        String(nextLastName || "").trim() !== String(existingUser?.lastName || "").trim();
      const phoneChanged =
        String(nextPhone || "").trim() !== String(existingUser?.phoneNumber || "").trim();

      let clientPatch = null;
      if (isPortalClient && linkedClientId && portalContactEmail && (nameChanged || phoneChanged)) {
        const clientSnap = await getDoc(doc(firebase, COLLECTIONS.CLIENT, linkedClientId));
        if (clientSnap.exists()) {
          clientPatch = buildClientPatchForPortalUserUpdate(
            clientSnap.data(),
            portalContactEmail,
            {
              firstName: nextFirstName,
              lastName: nextLastName,
              phoneNumber: nextPhone,
              nameChanged,
              phoneChanged,
            }
          );
        }
      }

      if (clientPatch) {
        await commitPortalUserAndClientUpdate({
          userDocRef,
          userUpdateData: updateData,
          clientId: linkedClientId,
          contactEmail: portalContactEmail,
          clientPatch,
        });
      } else {
        await setDoc(userDocRef, updateData, { merge: true });
      }

      return { status: true, data: { ...formData, id } };
    }

    const emailQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("email", "==", formData?.email),
      where("status", "==", true),
      limit(1)
    );

    const phoneQuery = query(
      collection(firebase, COLLECTIONS.USERS),
      where("phoneNumber", "==", formData?.phoneNumber),
      where("status", "==", true),
      limit(1)
    );

    const [emailSnapshot, phoneSnapshot] = await Promise.all([
      getDocs(emailQuery),
      getDocs(phoneQuery),
    ]);

    if (!emailSnapshot.empty) {
      return {
        status: false,
        field: "email",
        message: "A user with this email already exists.",
      };
    }

    if (!phoneSnapshot.empty) {
      return {
        status: false,
        field: "phone",
        message: "A user with this phone number already exists.",
      };
    }

    const isSuperUserCreate = formData?.role === "superUser";
    let companyCountryForPhone = formData.country;
    if (formData.companyId) {
      try {
        const companySnap = await getDoc(
          doc(firebase, COLLECTIONS.USERS, String(formData.companyId))
        );
        if (companySnap.exists()) {
          companyCountryForPhone =
            companySnap.data()?.country || companyCountryForPhone;
        }
      } catch (companyCountryError) {
        console.error("Failed to resolve company country:", companyCountryError);
      }
    }
    let phoneFields = normalizePhoneFields(formData, companyCountryForPhone);
    if (!isSuperUserCreate && formData?.phoneNumber) {
      const phoneCheck = validateAndNormalizePhone(formData, companyCountryForPhone);
      if (!phoneCheck.ok) {
        return {
          status: false,
          field: "phone",
          message: phoneCheck.error,
        };
      }
      phoneFields = phoneCheck.fields;
      formData.phoneNumber = phoneFields.phoneNumber;
      formData.country = phoneFields.country;
      formData.phoneCountryCode = phoneFields.phoneCountryCode;
    }
    const phoneNational = phoneFields.phoneNumber;
    const phoneAuth = phoneForFirebaseAuth(
      phoneNational,
      phoneFields.country,
      phoneFields.phoneCountryCode
    );

    const newCompanyData = {
      firstname: formData.firstName,
      lastname: formData.lastName,
      email: formData.email,
      phoneNumber: phoneNational,
      country: phoneFields.country,
      phoneCountryCode: phoneFields.phoneCountryCode,
      role: formData.role,
      password: formData.password,
    };

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const CreatedUser = await axiosInstance.post(
      `${BaseUrl}createUser`,
      newCompanyData,
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (CreatedUser?.data?.status === true) {
      const data = {
        ...formData,
        role: formData.role || "",
        userId: CreatedUser?.data?.uid,
        firstName: formData?.firstName || "",
        lastName: formData?.lastName || "",
        email: formData?.email || "",
        userName: formData?.email || "",
        phoneNumber: phoneNational || "",
        country: phoneFields.country,
        phoneCountryCode: phoneFields.phoneCountryCode,
        status: true,
        ...(formData.role === "admin" && { companyId: formData.companyId }),
      };
      delete data.password;
      delete data.confirmPassword;

      const create_user_res = await createUsers(data);
      if (create_user_res?.status === true) {
        newData = create_user_res;
        return { status: true, data: newData };
      } else {
        throw new Error(create_user_res.error || "Unknown error");
      }
    }

    return { status: false, message: "Failed to create user." };
  } catch (error) {
    console.error("saveOrUpdateUserData Error:", error);
    return {
      status: false,
      message: getUserSaveErrorMessage(error),
    };
  }
}

export async function deleteUsers(id, subscriptionId = null, subscriptionItemId = null) {
  try {
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;
    const userDocRef = doc(firebase, COLLECTIONS.USERS, id);
    const userSnapshot = await getDoc(userDocRef);
    const firstName = userSnapshot.data()?.firstName || "";
    const email = userSnapshot.data()?.email || "";
    const companyId = userSnapshot.data()?.companyId || "";

    if (!currentUserId) {
      throw new Error("No authenticated user found.");
    }

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}removeUser`,
      { id: id, firstName, email },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );

    if (!res.data.status) {
      throw new Error("Failed to delete user from Firebase Authentication");
    }

    const updateData = {
      deleted: true,
      status: false,
      updatedBy: currentUserId,
      deletedBy: currentUserId,
      deletedAt: serverTimestamp(),
    };

    await updateDoc(userDocRef, updateData);

    if(subscriptionId && subscriptionItemId){
      await updateStripeQuantity(subscriptionId, subscriptionItemId, companyId);
    }
    return { status: true };
  } catch (error) {
    return {
      status: false,
      error:
        error.message || "An unknown error occurred while deleting the user.",
    };
  }
}

export async function deleteCompanyUserAccount(email) {
  try {
    const userRef = collection(firebase, COLLECTIONS.USERS);
    const q = query(userRef, where("email", "==", email));
    const snap = await getDocs(q);

    if (snap.empty) {
      throw new Error("User not found");
    }

    const userDoc = snap.docs[0];
    const userId = userDoc.id;
    const userData = userDoc.data();

    const {firstName, customer } = userData;

    const res = await axiosInstance.post(`${BaseUrl}removeUser`, {
      id: userId,
      firstName,
      email,
      update: true
    });

    if (!res.data.status) {
      throw new Error(res.data.message || "Failed to delete user");
    }

    if (customer) {
      const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
      await axiosInstance.post(`${BaseUrl}stripeProxyDeleteCustomer`, { customerId: customer }, { headers: { ...defaultHeaders, ...authHeaders } });
    }

    return { status: true };
  } catch (error) {
    console.error("RemoveCompanyUserAccount error:", error);
    return { status: false, error: error.message };
  }
}

export async function createStripeLink(id, subscriptionId) {
  try {
    if (!id) throw new Error("Customer ID is required.");
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyCreateBillingPortalSession`, {
      customerId: id,
      returnUrl: `https://${DOMAINURL}`,
      flowType: "subscription_cancel",
      subscriptionId,
    }, { headers: { ...defaultHeaders, ...authHeaders } });
    return { url: res?.data?.url ?? null };
  } catch (error) {
    console.error("Error creating customer portal session:", error);
    return { url: null };
  }
}

export async function createUpdatePlanStripeLink(id, subscriptionId) {
  try {
    if (!id) throw new Error("Customer ID is required.");
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyCreateBillingPortalSession`, {
      customerId: id,
      returnUrl: `https://${DOMAINURL}`,
      flowType: "subscription_update",
      subscriptionId,
    }, { headers: { ...defaultHeaders, ...authHeaders } });
    return { url: res?.data?.url ?? null };
  } catch (error) {
    console.error("Error creating customer portal session:", error);
    return { url: null };
  }
}

export async function CreateUpdateLink(id) {
  try {
    if (!id) throw new Error("Customer ID is required.");
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyCreateCheckoutSession`, {
      customerId: id,
      returnUrl: `https://${DOMAINURL}`,
    }, { headers: { ...defaultHeaders, ...authHeaders } });
    return { url: res?.data?.url ?? null };
  } catch (error) {
    console.error("Error creating customer portal session:", error);
    return { url: null };
  }
}

export async function getAppConfig() {
  const now = Date.now();
  if (appConfigCache && now - appConfigCacheTs < APP_CONFIG_CACHE_TTL_MS) {
    return appConfigCache;
  }
  const cached = readSessionCache(APP_CONFIG_SESSION_KEY, APP_CONFIG_CACHE_TTL_MS);
  if (cached) return cached;

  if (appConfigInflight) {
    return appConfigInflight;
  }

  appConfigInflight = (async () => {
    try {
      const res = await axiosInstance.get(`${BaseUrl}getAppConfig`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = {
        success: res?.data?.status === true,
        data: res?.data?.data ?? {},
      };
      if (result.success) {
        appConfigCache = result;
        appConfigCacheTs = Date.now();
        writeSessionCache(APP_CONFIG_SESSION_KEY, result);
      }
      return result;
    } catch (error) {
      console.error("Error fetching app config:", error);
      return { success: false, data: {} };
    } finally {
      appConfigInflight = null;
    }
  })();

  return appConfigInflight;
}

export async function getStripeConfigs() {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };
    const res = await axiosInstance.get(`${BaseUrl}getStripeConfig`, {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      skipAuthRedirect: true,
    });

    return {
      success: true,
      status: res.status,
      data: res.data.data,
    };
  } catch (error) {
    console.error("Error fetching Stripe config:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to fetch config",
    };
  }
}

export async function CreateStripeSubscription(email, priceId) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}createSubscription`,
      {
        email,
        priceId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res.data.data,
    };
  } catch (error) {
    console.error("Error creating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to create subscription",
    };
  }
}

export async function createNewSubscriptionAfterCancel(
  customerId,
  newPriceId,
  promoCode,
  options = {},
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };
    const { promoExplicitlyApplied = false } = options;

    const res = await axiosInstance.post(
      `${BaseUrl}createNewSubscription`,
      {
        customerId,
        newPriceId,
        promoCode: promoCode || undefined,
        promoExplicitlyApplied: Boolean(promoExplicitlyApplied),
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function createRepurchasePartnerScheduleSubscription(
  customerId,
  newPriceId,
  promoCode,
  options = {},
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };
    const { promoExplicitlyApplied = false } = options;

    const res = await axiosInstance.post(
      `${BaseUrl}createRepurchasePartnerScheduleSubscription`,
      {
        customerId,
        newPriceId,
        promoCode: promoCode || undefined,
        promoExplicitlyApplied: Boolean(promoExplicitlyApplied),
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error creating repurchase partner schedule:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error:
        error.response?.data?.message ||
        "Failed to create partner schedule subscription",
    };
  }
}

export async function cancelRepurchaseDraftSubscription(subscriptionId) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}cancelRepurchaseDraftSubscription`,
      { subscriptionId },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data || null,
      message: res?.data?.message || "Draft subscription canceled",
    };
  } catch (error) {
    console.error("Error cancelling repurchase draft subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error:
        error.response?.data?.message ||
        "Failed to cancel repurchase draft subscription",
    };
  }
}

export async function gettheSetupIntent(
  customerId
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}createSetupIntent`,
      {
        customerId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

const currentPhaseCache = new Map();
const CURRENT_PHASE_CACHE_MS = 60 * 1000;

export async function getCurrentPhase(subscriptionId) {
  if (!subscriptionId) {
    return { success: false, currentPhaseIndex: null, phases: [] };
  }

  const cached = currentPhaseCache.get(subscriptionId);
  if (cached && Date.now() - cached.at < CURRENT_PHASE_CACHE_MS) {
    return cached.value;
  }

  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}stripeProxyGetSubscriptionSchedule`,
      { subscriptionId },
      {
        headers: { ...defaultHeaders, ...authHeaders },
        skipAuthRedirect: true,
      }
    );
    const value = {
      success: true,
      currentPhaseIndex: res?.data?.currentPhaseIndex ?? null,
      phases: res?.data?.phases ?? [],
    };
    currentPhaseCache.set(subscriptionId, { at: Date.now(), value });
    return value;
  } catch (error) {
    const value = {
      success: false,
      currentPhaseIndex: null,
      phases: [],
      status: error?.response?.status || 500,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to fetch subscription schedule",
    };
    currentPhaseCache.set(subscriptionId, { at: Date.now(), value });
    return value;
  }
}


export async function UpdateStripeScheduleSubscription(
  subscriptionId,
  selectedProductId,
  normalPrice
) {
  try {
    if (!subscriptionId || !selectedProductId || !normalPrice) {
      return {
        success: false,
        status: 400,
        error: "Missing subscription schedule update params",
        message: "Missing subscription schedule update params",
      };
    }

    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateSubscriptionSchedulePlan`,
      {
        subscriptionId,
        selectedProductId,
        normalPrice,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      "Failed to update subscription";
    return {
      success: false,
      status: error.response?.status || 500,
      error: errorMessage,
      message: errorMessage,
    };
  }
}

export async function UpgradeStripeSubscription(
  subscriptionId,
  newPriceId,
  promoCode,
  paymentMethodId
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateSubscription`,
      {
        subscriptionId,
        newPriceId,
        promoCode,
        paymentMethodId
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      downgrade: res?.data?.downgrade,
      data: res?.data?.data,
      switch: res?.data?.switch,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function UpgradeQuantitySubscription(
  customerId,
  newPriceId,
  promoCode,
  quantity
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateQuantitySubscription`,
      {
        customerId,
        newPriceId,
        promoCode,
        quantity,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function UpgradeNewQuantitySubscription(
  subscriptionId,
  newPriceId,
  promoCode,
  quantity
) {
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };

    const res = await axiosInstance.post(
      `${BaseUrl}updateNewQuantitySubscription`,
      {
        subscriptionId,
        newPriceId,
        promoCode,
        quantity,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      status: res.status,
      data: res?.data?.data,
    };
  } catch (error) {
    console.error("Error updating Stripe subscription:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription",
    };
  }
}

export async function calculateSubscriptionPayment(subscriptionId, newPriceId, promoCode) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const res = await axiosInstance.post(`${BaseUrl}stripeProxyCalculateSubscriptionPayment`, {
    subscriptionId,
    newPriceId,
    promoCode,
  }, { headers: { ...defaultHeaders, ...authHeaders } });
  return res.data;
}

async function postTransactionalEmail(payload = {}) {
  const token = getLoginToken();
  const headers = { "Content-Type": "application/json" };
  const cleanPayload = { ...payload };

  if (cleanPayload.companyId == null || cleanPayload.companyId === "") {
    delete cleanPayload.companyId;
  }

  if (!cleanPayload?.email || !cleanPayload?.subject || !cleanPayload?.contenttxt) {
    throw new Error("Missing email payload fields");
  }

  if (token) {
    try {
      return await axiosInstance.post(`${BaseUrl}sendEmailData`, cleanPayload, {
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      if (error?.response?.status !== 401 && error?.response?.status !== 403) {
        throw error;
      }
    }
  }

  return axiosInstance.post(`${BaseUrl}sendEmailData`, cleanPayload, { headers });
}


export async function PaymentFailedEmail(formData, paymentMethod) {
  const dateTime = moment().format("MM/DD/YY");
  let emailContent = "";

  if (paymentMethod === "ach") {
    emailContent = `
      <p>Hello ${formData.name},</p>

      <p>Unfortunately, this bank transaction has failed.</p>

      <p>Please check your bank information and try again. 
      If the payment continues to fail, please contact your banking institution for assistance.</p>

      <p>If you have any questions or need help, feel free to reply to this email — we're here to support you.</p>

      <p>Thank you,<br/>ProTeams Billing Team</p>
    `;
  }

  else {
    emailContent = `
      <p>Hello ${formData.name},</p>

      <p>We were unable to process your recent payment for your ProTeams subscription.</p>

      <p>Please double-check your card details and try the payment again. 
      If the issue persists, consider using a different card or contacting your card provider for assistance.</p>

      <p>If you need any help, simply reply to this email — our team is always here to support you.</p>

      <p>Thank you,<br/>The ProTeams Billing Team</p>
    `;
  }

  const { subject, content } = EmailTemplate({
    subject: `ProTeams - (${formData.companyName}) UnSuccessful Payment (${dateTime})`,
    content: emailContent,
  });
  try {
    await postTransactionalEmail({
      companyId: formData?.userId || formData?.companyId,
      email: formData?.email,
      subject,
      contenttxt: content,
    });

    return { success: true };
  } catch (error) {
    console.error("Error sending payment failed email:", error);
    return { success: false, error };
  }
}

export async function SuccesDownGradeEmail(formData, data, subscriptionId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const subRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveSubscription`, { subscriptionId }, { headers: { ...defaultHeaders, ...authHeaders } });
    const subscription = subRes.data;
    const prodRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveProduct`, { productId: subscription.items.data[0].price.product }, { headers: { ...defaultHeaders, ...authHeaders } });
    const planName = prodRes.data.name;

    const {
      currentBalanceAfterCredit,
    } = data;

    const formatStripeAmount = (amount) => {
      return `$${(amount / 100).toFixed(2)}`;
    };

    const { subject, content } = EmailTemplate({
      subject: `Your ${formData?.companyName}'s Subscription has Been Downgraded`,
      content: `
        <p>Hello ${formData?.name},</p>

        <p>We’re writing to confirm that your subscription has been successfully downgraded to the <strong>${planName}</strong> plan. This change has been applied, and future billing will reflect this updated package.</p>

        ${
          currentBalanceAfterCredit > 0
            ? `<p><strong>Account Credit:</strong> ${formatStripeAmount(currentBalanceAfterCredit)} has been applied to your account. This credit will automatically be used toward your upcoming billing cycle.</p>`
            : ""
        }

        <p>If you have any questions or need further assistance, please don’t hesitate to reach out. We're here to help!</p>

        <p>Thank you for choosing <strong>ProTeams</strong>. We value your continued support.</p>

        <p>Best regards,<br/>The ProTeams Billing Team</p>
      `,
    });

    try {
      await postTransactionalEmail({
        companyId: formData?.userId || formData?.companyId,
        email: formData?.email,
        subject,
        contenttxt: content,
      });
      return { success: true, emailSent: true };
    } catch (emailError) {
      // Email delivery should not block the completed downgrade flow.
      console.warn("Downgrade completed, but confirmation email failed:", emailError?.response?.status || emailError?.message);
      return { success: true, emailSent: false, emailError };
    }
  } catch (error) {
    console.error("Error sending downgrade confirmation email:", error);
    return { success: false, error };
  }
}

export async function SuccesUpdateSubscriptionEmail(formData) {
  try {
    const { subject, content } = EmailTemplate({
      subject: "Your Subscription Has Been Changed",
      content: `
     <p>Hello ${formData?.name},</p>

      <p>We're writing to confirm that your subscription has been successfully updated. Your changes have been applied, and there is no additional payment required at this time.</p>

      <p>You can continue to enjoy the updated benefits of your plan without any interruption. Your billing will reflect the new subscription details going forward.</p>

      <p>If you have any questions or need assistance with your account, please don't hesitate to reach out to our support team.</p>

      <p>Thank you for choosing ProTeams We appreciate your continued trust in our services.</p>

      <p>Best regards,<br/>The ProTeams Support Team</p>
    `,
    });
    await postTransactionalEmail({
      companyId: formData?.userId || formData?.companyId,
      email: formData?.email,
      subject,
      contenttxt: content,
    });

    return { success: true };
  } catch (error) {
    console.error("Error sending payment failed email:", error);
    return { success: false, error };
  }
}

export async function sendPartnerSubscriptionSuccessEmail(formData = {}, planName = "") {
  try {
    const safeName = formData?.name || "there";
    const safePlanName = planName || "your selected";
    const safeEmail = formData?.email;
    const safeCompany = formData?.companyName || "ProTeams";
    const companyId = formData?.userId || formData?.companyId;

    if (!safeEmail) {
      return { success: false, error: "Missing recipient email" };
    }

    const { subject, content } = EmailTemplate({
      subject: `Founding Partner Plan Activated - ${safeCompany}`,
      content: `
        <p>Hello ${safeName},</p>

        <p>Your Founding Partner subscription update was completed successfully. You are now enrolled in the <strong>${safePlanName}</strong> plan with partner benefits active.</p>

        <p><strong>What this means:</strong></p>
        <ul>
          <li>Your partner pricing schedule is now applied to your account.</li>
          <li>Your plan features have been updated successfully.</li>
          <li>Future billing will follow your Founding Partner benefit timeline.</li>
        </ul>

        <p>If you have any questions, please reply to this email and our billing team will help.</p>

        <p>Best regards,<br/>The ProTeams Billing Team</p>
      `,
    });

    await postTransactionalEmail({
      companyId,
      email: safeEmail,
      subject,
      contenttxt: content,
    });

    return { success: true };
  } catch (error) {
    console.error("Error sending partner subscription success email:", error);
    return { success: false, error };
  }
}

export async function sendEmailForAchPaymentDetail(
  newPriceId,
  customerId,
  newAmounts
) {
  const colRef = collection(firebase, COLLECTIONS.USERS);
  const q = query(colRef, where("customer", "==", customerId));
  const customerQuerySnapshot = await getDocs(q);
  const userData = customerQuerySnapshot.docs[0]?.data();

  const customerEmail = userData?.email;
  const companyName = `${userData?.companyName}`;
  const customerName = userData?.firstName;
  const dateTime = moment().format("MM/DD/YY");

  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const priceRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrievePrice`, { priceId: newPriceId }, { headers: { ...defaultHeaders, ...authHeaders } });
  const price = priceRes.data;
  const prodRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveProduct`, { productId: price.product }, { headers: { ...defaultHeaders, ...authHeaders } });
  const planName = prodRes.data.name;
  const currentAmount = price.unit_amount / 100;
  const newPaymentAmount = newAmounts / 100;

  const content = `
    <p>Hi ${customerName},</p>

    <p>Your ACH payment request for the <strong>${planName}</strong> subscription has been received and is now being processed.</p>

    <p>Below are the payment details:</p>
    <ul>
      <li><strong>Plan:</strong> ${planName}</li>
      <li><strong>New Plan Amount:</strong> $${currentAmount.toFixed(2)}</li>
      <li><strong>Total Payable Amount (after discount):</strong> $${newPaymentAmount.toFixed(2)}</li>
    </ul>

    <p>Please note:</p>
    <p>ACH transactions typically take <strong>few business days</strong> to complete. Once the payment is successfully processed, your subscription status and billing details will automatically update in the system.</p>

    <p>If you have any questions or need assistance, feel free to reach out.</p>

    <p>Best regards,<br/>
    The ProTeams Team</p>
  `;

  const { subject: emailSubject, content: emailContent } = EmailTemplate({
    subject: `ProTeams – (${companyName}) ACH Payment Processing (${dateTime})`,
    headline: `ACH Payment Processing`,
    content,
  });

  await postTransactionalEmail({
    companyId: userData?.userId || userData?.companyId,
    email: customerEmail,
    subject: emailSubject,
    contenttxt: emailContent,
  });
}


export async function finalizeFreeSubscription(
  cardDetails,
  formData,
  subscriptionId,
  newPriceId,
  newProduct,
  setupIntentId,
  paymentMethodType,
  code,
  userId
) {
  try {

    const cardCollectionRef = collection(firebase, COLLECTIONS.STRIPE_CARDS);
    await addDoc(cardCollectionRef, {
      card: cardDetails,
      createdAt: new Date(),
    });

    const userDocRef = doc(firebase, COLLECTIONS.USERS, userId);
    await setDoc(userDocRef, {
      userPromoCode: code,
    }, { merge: true });

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const siRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveSetupIntent`, { setupIntentId }, { headers: { ...defaultHeaders, ...authHeaders } });
    const setupIntent = siRes.data;

    if (setupIntent.status !== "succeeded") {
      console.error("SetupIntent not successful");
    }

    const paymentMethodId = setupIntent.payment_method;
    const customerId = setupIntent.customer;

    await axiosInstance.post(`${BaseUrl}stripeProxyUpdateCustomerBalance`, {
      customerId,
      balance: 0,
      defaultPaymentMethodId: paymentMethodId,
    }, { headers: { ...defaultHeaders, ...authHeaders } });

    await axiosInstance.post(
      `${BaseUrl}updateSubscriptionSchedulePlan`,
      {
        subscriptionId: subscriptionId,
        selectedProductId: newProduct,
        normalPrice: newPriceId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    )

    const commonHtml = 
    `<h3> Welcome, ${formData?.name}</h3>
      <p>
      Thank you for partnering with us.
      </p>

      <p>
      As part of your partnership benefits, you’ve been enrolled in <strong>6 months of complimentary access</strong> to our platform.
      </p>

      <p>
      <strong>You will not be charged during this free period.</strong>
      </p>

      <p>
      We’ve securely saved your payment details on file for future billing reference once your complimentary access ends.
      </p>

      <h3>What This Means</h3>

      <p>• You have full access for 6 months at no cost.</p>
      <p>• No payment has been processed.</p>
      <p>• Billing will begin only after your free period ends.</p>
      <p>• You will receive a notification before any future charge.</p>`
    

      const achBlock = 
      `<h3>Bank Account Verification Required</h3>
      <p>
      Since you chose to add a bank account (ACH), a quick verification step is required.
      </p>

      <p>
      Stripe will send two small micro-deposits to your bank account within 1–2 business days.
      </p>

      <p>
      Once you receive them:
      </p>

      <p>• Log in to your account.</p>
      <p>• Enter the deposit amounts to verify your bank account.</p>

      <p>
      No charges will occur during this verification process.
      </p>
      `

      const closingBlock = 
      `<p>
        Your payment information is securely encrypted and processed by Stripe, a certified PCI-compliant payment provider.
        </p>

        <p>
        If you need to update your payment method at any time, you can do so from your account settings.
        </p>

        <p>
        We’re excited to have you as a partner and look forward to growing together.
        </p>

        <p>
        Best regards,<br/>
        <strong>ProTeams Team</strong>
        </p>
        `
      let emailBody = commonHtml;

      if (paymentMethodType === "ach") {
        emailBody += achBlock;
      }

      emailBody += closingBlock;

    const { subject: emailSubject, content: emailContent } =
      EmailTemplate({
        subject: "Welcome Partner – Your Free Access Has Been Activated",
        content: emailBody,
      });

    postTransactionalEmail({
      companyId: userId || formData?.companyId,
      email: formData.email,
      subject: emailSubject,
      contenttxt: emailContent,
    }).catch(err => {
      console.error("Background email error:", err?.response?.data || err.message);
    });


    return { success: true };

  } catch (error) {
    console.error("Error finalizing free subscription:", error);
    return { success: false };
  }
}


export async function saveCardDetails(
  cardDetails,
  formData,
  subscriptionId,
  newPriceId,
  newAmounts,
  paymentMethodId,
  paymentMethodType,
  code,
  userId
) {
  try {
    const cardCollectionRef = collection(firebase, COLLECTIONS.STRIPE_CARDS);
    await addDoc(cardCollectionRef, {
      card: cardDetails,
      createdAt: new Date(),
    });

    if (code && userId) {
      const userDocRef = doc(firebase, COLLECTIONS.USERS, userId);
      await setDoc(userDocRef, {
        userPromoCode: code,
      }, { merge: true });
    }

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const subRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveSubscription`, { subscriptionId }, { headers: { ...defaultHeaders, ...authHeaders } });
    const subscriptionBeforeUpdate = subRes.data;
    const oldStatus = subscriptionBeforeUpdate.status;
    const currentItemId = subscriptionBeforeUpdate.items.data[0].id;
    const customerId = subscriptionBeforeUpdate.customer;
    const PriceIds = [
      "price_1R1Pm6H54HzvHwSdzS4aA0s9",
      "price_1RGH20H54HzvHwSdZTHWjNHO",
    ];

    const custRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveCustomer`, { customerId }, { headers: { ...defaultHeaders, ...authHeaders } });
    const customer = custRes.data;
    const currentDefault = customer?.invoice_settings?.default_payment_method;

    if (paymentMethodType === "ach") {
      // await stripe.paymentMethods.attach(paymentMethodId, { customer: customerId });

      // if (!currentDefault) {
      //   await stripe.customers.update(customerId, {
      //     invoice_settings: { default_payment_method: paymentMethodId }
      //   });
      // }

      // await stripe.subscriptions.update(subscriptionId, {
      //   items: [{ id: currentItemId, price: newPriceId }],
      //   proration_behavior: "none",
      //   billing_cycle_anchor: "now",
      //   trial_end: "now",
      // });

      return { success: true };
    } else {
      await axiosInstance.post(`${BaseUrl}stripeProxyAttachPaymentMethod`, { paymentMethodId, customerId }, { headers: { ...defaultHeaders, ...authHeaders } });
    }

    if (!currentDefault) {
      await axiosInstance.post(`${BaseUrl}stripeProxyUpdateCustomerBalance`, {
        customerId,
        balance: 0,
        defaultPaymentMethodId: paymentMethodId,
      }, { headers: { ...defaultHeaders, ...authHeaders } });
    }

    if (PriceIds.includes(newPriceId)) {
      console.log("Price match found. Skipping subscription update.");
    } else {
      if (oldStatus === "trialing") {
        await axiosInstance.post(`${BaseUrl}stripeProxyUpdateSubscription`, {
          subscriptionId,
          updates: {
            trial_end: "now",
            proration_behavior: "none",
            billing_cycle_anchor: "now",
            items: [{ id: currentItemId, price: newPriceId }],
          },
        }, { headers: { ...defaultHeaders, ...authHeaders } });
      } else if (oldStatus !== "trialing" && oldStatus !== "canceled") {
        await axiosInstance.post(`${BaseUrl}stripeProxyUpdateSubscription`, {
          subscriptionId,
          updates: {
            items: [{ id: currentItemId, price: newPriceId }],
            proration_behavior: "none",
            billing_cycle_anchor: "now",
          },
        }, { headers: { ...defaultHeaders, ...authHeaders } });
      }
    }
    const subRes2 = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveSubscription`, { subscriptionId }, { headers: { ...defaultHeaders, ...authHeaders } });
    const subscription = subRes2.data;
    const colRef = collection(firebase, COLLECTIONS.USERS);

    const q = query(colRef, where("customer", "==", customerId));

    const customerQuerySnapshot = await getDocs(q);
    const userData = customerQuerySnapshot.docs[0]?.data();
    const customerEmail = userData?.email;
    const companyName = `${userData?.companyName}`;
    const dateTime = moment().format("MM/DD/YY");

    const customerName = userData?.firstName;

    const prodRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveProduct`, { productId: subscription.items.data[0].price.product }, { headers: { ...defaultHeaders, ...authHeaders } });
    const planName = prodRes.data.name;
    const paymentAmount = subscription.items.data[0].price.unit_amount / 100;
    const paymentNewAmount = newAmounts / 100;
    const nextPaymentDate = new Date(subscription.current_period_end * 1000);
    const formattedNextPaymentDate = `${
      nextPaymentDate.getMonth() + 1
    }/${nextPaymentDate.getDate()}/${nextPaymentDate.getFullYear()}`;

    const emailPromises = [];

    if (["trialing", "canceled"].includes(oldStatus)) {
      const _subject =
        oldStatus === "trialing"
          ? `ProTeams - (${companyName}) Subscription Activated (${dateTime})`
          : `ProTeams - (${companyName}) Subscription Renewed (${dateTime})`;

      const _content = `
        <p>Hi ${customerName},</p>
    
        <p>${
          oldStatus === "trialing"
            ? "We're excited to let you know that your free trial has been successfully upgraded to the"
            : "We're pleased to inform you that your subscription to the"
        } <strong>${planName}</strong> plan is now active.</p>
    
        <p>Here are your subscription details:</p>
        <ul>
          <li><strong>Plan:</strong> ${planName}</li>
          <li><strong>Payment Amount:</strong> $${paymentAmount.toFixed(2)}</li>
          <li><strong>Next Billing Date:</strong> ${formattedNextPaymentDate}</li>
        </ul>
    
        <p>You can manage your subscription, update payment details, or make changes at any time by logging into your ProTeams account.</p>
    
        <p>Thank you for choosing ProTeams—we’re excited to support your team’s success.</p>
    
        <p>Best regards,<br/>
        The ProTeams Team</p>
    
        <p>
          <a href="mailto:support@proteams.io">support@proteams.io</a> |
          <a href="https://www.proteams.io">www.proteams.io</a>
        </p>
      `;

      const { subject: emailSubject, content: emailContent } = EmailTemplate({
        subject: _subject,
        headline: _subject,
        content: _content,
      });

      emailPromises.push(
        postTransactionalEmail({
          companyId: userData?.userId || userData?.companyId || userId,
          email: customerEmail,
          subject: emailSubject,
          contenttxt: emailContent,
        })
      );

    } else if (oldStatus === "active"){
      const subject = `ProTeams - (${companyName}) Subscription Activated (${dateTime})`;

      const content = `
        <p>Hi ${customerName},</p>
    
        <p>${"We're pleased to inform you that your subscription to the"} <strong>${planName}</strong> plan is now active.</p>
    
        <p>Here are your subscription details:</p>
        <ul>
          <li><strong>Plan:</strong> ${planName}</li>
          <li><strong>Plan Amount:</strong> $${paymentAmount.toFixed(2)}</li>
          <li><strong>Total Payable Amount (after discount):</strong> $${paymentNewAmount.toFixed(2)}</li>
        </ul>
    
        <p>You can manage your subscription, update payment details, or make changes at any time by logging into your ProTeams account.</p>
    
        <p>Thank you for choosing ProTeams—we’re excited to support your team’s success.</p>
    
        <p>Best regards,<br/>
        The ProTeams Team</p>
      `;

      const { subject: emailSubject, content: emailContent } = EmailTemplate({
        subject,
        headline: subject,
        content,
      });

      emailPromises.push(
        postTransactionalEmail({
          companyId: userData?.userId || userData?.companyId,
          email: customerEmail,
          subject: emailSubject,
          contenttxt: emailContent,
        })
      );
    }

    const paymentSubject = `ProTeams - (${companyName}) Successful Payment (${dateTime})`;
    const paymentContent = `
      <p>Dear ${formData.name},</p>

    <p>We are pleased to inform you that we have successfully processed your payment, and your ProTeams subscription has been updated accordingly.</p>

    <p>Thank you for being an essential part of the ProTeams community. We’re excited to continue supporting your operations and help you achieve even more success.</p>

    <p>If you have any questions or need further assistance, please don't hesitate to reach out.</p>

    <p>Best regards, <br>The ProTeams Billing Team</p>

`;

    const { subject: paymentEmailSubject, content: paymentEmailContent } =
      EmailTemplate({
        subject: paymentSubject,
        headline: paymentSubject,
        content: paymentContent,
      });

    emailPromises.push(
      postTransactionalEmail({
        companyId: userData?.userId || userData?.companyId || userId,
        email: formData.email,
        subject: paymentEmailSubject,
        contenttxt: paymentEmailContent,
      })
    );

    if (emailPromises.length > 0) {
      const emailResults = await Promise.allSettled(emailPromises);
      const failedEmails = emailResults.filter(
        (result) => result.status === "rejected"
      );

      // Email delivery failures should not fail a completed payment/subscription update.
      if (failedEmails.length > 0) {
        console.warn(
          `Subscription updated, but ${failedEmails.length} confirmation email(s) failed.`,
          failedEmails.map((result) => result?.reason?.response?.status || result?.reason?.message)
        );
      }
    }

    return { success: true };
  } catch (error) {
    console.error("Error saving card details:", error);
    return { success: false, error };
  }
}

export async function findSubscriptionByCustomerId(customerId) {
  try {
    const subsRef = collection(firebase, COLLECTIONS.STRIPE_SUBSCRIPTION);

    const q = customerId
      ? query(subsRef, where("customer", "==", customerId))
      : query(subsRef);

    const querySnapshot = await getDocs(q);

    const subscriptions = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return { status: "success", data: subscriptions };
  } catch (error) {
    console.error("Error finding subscriptions:", error);
    return { status: "error", message: error.message };
  }
}
export async function fetchSubscriptionWithCustomer(customerId) {
  try {
    if (!customerId) {
      return { status: "success", data: [] };
    }

    const subsRef = collection(firebase, COLLECTIONS.STRIPE_SUBSCRIPTION);
    const q = query(subsRef, where("customer", "==", customerId));

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const subscriptions = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      return { status: "success", data: subscriptions };
    } else {
      return { status: "success", data: [] };
    }
  } catch (error) {
    console.error("Error finding subscriptions:", error);
    return { status: "error", message: error.message };
  }
}

export async function getSubscriptionSchedule(scheduleId) {
  try {
    if (!scheduleId) return null;
    const scheduleRef = doc(firebase, COLLECTIONS.STRIPE_SUBSCRIPTION_SCHEDULES, scheduleId);
    const scheduleSnap = await getDoc(scheduleRef);
    if (scheduleSnap.exists()) {
      return { id: scheduleSnap.id, ...scheduleSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching subscription schedule:", error);
    return null;
  }
}


export async function getCardDetails(paymentIntentId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyListCharges`, { paymentIntentId }, { headers: { ...defaultHeaders, ...authHeaders } });
    return res.data;
  } catch (err) {
    console.error("Error fetching card details:", err.message);
    return null;
  }
}

export async function getBankAccountDetails (paymentIntent) {
  try {
    const details = paymentIntent?.charges?.data?.[0]?.payment_method_details;

    if (!details || details.type !== "us_bank_account") {
      console.error("Invalid ACH data");
    }

    return {
      type: "bank_account",
      bankName: details.us_bank_account.bank_name,
      last4: details.us_bank_account.last4,
      routingNumber: details.us_bank_account.routing_number,
      fingerprint: details.us_bank_account.fingerprint,
    };
  } catch (err) {
    console.error("ACH parse error:", err.message);
    return {};
  }
};

export async function CancelStripeSubscription(subscriptionId, customerId, formData){
  try {
    const authHeaders = {
      Authorization: `Bearer ${getLoginToken()}`,
    };


    const userRef = collection(firebase, COLLECTIONS.USERS);
    const q = query(userRef, where("customer", "==", customerId));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const userDocRef = userDoc.ref;

      const cancelReason = {
        feedBack: formData?.feedBack || "",
        selectedOption: formData?.selectedOption || "",
        cancelAt: serverTimestamp()
      };

      await updateDoc(userDocRef, cancelReason);
    }

    const cancelResponse = await axiosInstance.post(
      `${BaseUrl}cancelSubscription`,
      {
        subscriptionId,
        selectedOption: formData?.selectedOption || "",
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    return {
      success: true,
      message: "Cancellation process initiated and saved",
      policy: cancelResponse?.data?.policy || null,
      data: cancelResponse?.data?.data || null,
    };
  } catch (error) {
    console.error("Error saving cancellation reason:", error);
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to update subscription info",
    };
  }
}

export async function getAmounts(clientSecretString) {
  try {
    const paymentIntentId = clientSecretString?.split("_secret")[0];
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyRetrievePaymentIntent`, { paymentIntentId }, { headers: { ...defaultHeaders, ...authHeaders } });
    return { amount: res.data?.amount ?? null };
  } catch (error) {
    console.error("Error retrieving payment intent:", error.message);
    return { amount: null, error: error.message };
  }
}

export async function getPaymentIntentStatus(clientSecretString) {
  try {
    const paymentIntentId = clientSecretString?.split("_secret")[0];
    if (!paymentIntentId) {
      return { status: null, paymentIntentId: null };
    }
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}stripeProxyRetrievePaymentIntent`,
      { paymentIntentId },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );
    return {
      status: res.data?.status || null,
      paymentIntentId,
    };
  } catch (error) {
    console.error("Error retrieving payment intent status:", error.message);
    return { status: null, error: error.message };
  }
}

export async function getPromotionCodes(code) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyListPromotionCodes`, { code }, { headers: { ...defaultHeaders, ...authHeaders } });
    return res.data;
  } catch (error) {
    console.error("Error fetching promo code:", error);
    return null;
  }
}

export async function getCustomerBalanceAmount(customerId) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const res = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveCustomer`, { customerId }, { headers: { ...defaultHeaders, ...authHeaders } });
  return res.data?.balance ?? 0;
}

export async function getFreemiumDowngradeEstimate(subscriptionId, customerId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}getFreemiumDowngradeEstimate`,
      { subscriptionId, customerId },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );
    return {
      success: true,
      status: res.status,
      data: res?.data?.data || {},
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to estimate freemium downgrade",
    };
  }
}

export async function transitionToFreemiumPlan(subscriptionId, customerId, idempotencyKey) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(
      `${BaseUrl}transitionToFreemium`,
      { subscriptionId, customerId, idempotencyKey },
      { headers: { ...defaultHeaders, ...authHeaders } }
    );
    if (res?.data?.status !== true) {
      return {
        success: false,
        status: res.status,
        error: res?.data?.message || "Failed to transition to freemium",
        data: res?.data?.data || {},
      };
    }
    return {
      success: true,
      status: res.status,
      data: res?.data?.data || {},
    };
  } catch (error) {
    return {
      success: false,
      status: error.response?.status || 500,
      error: error.response?.data?.message || "Failed to transition to freemium",
    };
  }
}

export async function getChargesData(invoice) {
  const paymentIntentId = invoice?.payment_intent;
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const piRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrievePaymentIntent`, { paymentIntentId }, { headers: { ...defaultHeaders, ...authHeaders } });
  const chargeId = piRes.data?.latest_charge;
  if (!chargeId) return null;
  const chargeRes = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveCharge`, { chargeId }, { headers: { ...defaultHeaders, ...authHeaders } });
  return chargeRes.data;
}

export async function getFreshInvoiceData (invoiceId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyRetrieveInvoice`, { invoiceId }, { headers: { ...defaultHeaders, ...authHeaders } });
    return res.data;
  } catch (error) {
    console.error("Error fetching invoice:", error);
    return { error: "Failed to fetch invoice" };
  }
};

export async function getCustomerPaymentMethods(customerId) {
  try {
    const res = await axiosInstance.post(
      `${BaseUrl}stripeProxyListPaymentMethods`,
      { customerId },
      { headers: { ...defaultHeaders }, skipAuthRedirect: true }
    );
    return {
      cards: res.data?.cards ?? [],
      defaultPaymentMethodId: res.data?.defaultPaymentMethodId ?? null,
    };
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    return { cards: [], defaultPaymentMethodId: null };
  }
}

export async function getPaymentMethod(customerId) {
  try {
    const res = await axiosInstance.post(
      `${BaseUrl}stripeProxyListPaymentMethods`,
      { customerId },
      { headers: { ...defaultHeaders }, skipAuthRedirect: true }
    );
    return { cards: res.data?.cards ?? [] };
  } catch (error) {
    console.error("Error fetching payment methods:", error);
    return { cards: [] };
  }
}


export async function attachPaymentMethod(paymentMethodId, customerId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(`${BaseUrl}stripeProxyAttachPaymentMethod`, { paymentMethodId, customerId }, { headers: { ...defaultHeaders, ...authHeaders } });
    return true;
  } catch (error) {
    console.error("Stripe attachPaymentMethod error:", error);
    throw error;
  }
}

export async function detachPaymentMethod(paymentMethodId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(`${BaseUrl}stripeProxyDetachPaymentMethod`, { paymentMethodId }, { headers: { ...defaultHeaders, ...authHeaders } });
    return true;
  } catch (error) {
    console.error("Error detaching payment method:", error);
    throw error;
  }
}

export async function updateDefaultPaymentMethod(paymentMethodId, customerId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(`${BaseUrl}stripeProxyUpdateDefaultPaymentMethod`, { paymentMethodId, customerId }, { headers: { ...defaultHeaders, ...authHeaders } });
    return true;
  } catch (error) {
    console.error("Error updating default payment method:", error);
    throw error;
  }
}

export async function getBankAccountSetupIntentFromServer(customerId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const response = await axiosInstance.post(
      `${BaseUrl}createACHSetupIntent`,
      { customerId },
      {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
      }
    );

    const setupIntentClientSecret = response.data?.setupIntentClientSecret;

    if (!setupIntentClientSecret) {
      console.error("Missing setupIntentClientSecret from server");
    }

    return setupIntentClientSecret;
  } catch (error) {
    console.error("Error fetching SetupIntent:", error);
    throw error;
  }
}

export async function attachAndVerifyCustomerBankAccount(
  setupIntentId,
  amounts,
  docId
) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(`${BaseUrl}stripeProxyVerifyMicrodeposits`, { intentId: setupIntentId, amounts }, { headers: { ...defaultHeaders, ...authHeaders } });

    const docRef = doc(firebase, COLLECTIONS.PAYMENT_VERIFICATION, docId);
    await updateDoc(docRef, {
      status: 'verified',
      verifiedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    console.error("ACH Verification Error:", error);
    return { success: false, message: error.message };
  }
}

export async function attachBankAccount(paymentMethodId, customerId, userId, paymentMethod) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    const res = await axiosInstance.post(`${BaseUrl}stripeProxyCreateSetupIntent`, {
      customerId,
      paymentMethodTypes: ["us_bank_account"],
      paymentMethodId,
    }, { headers: { ...defaultHeaders, ...authHeaders } });

    await addDoc(collection(firebase, COLLECTIONS.PAYMENT_VERIFICATION), {
      paymentMethodId,
      paymentMethod,
      setupIntentId: res.data.setupIntentId,
      userId,
      method: "pending_verification",
      status: "pending",
      createdAt: serverTimestamp(),
    });

    return {
      success: true,
      paymentMethodId,
      clientSecret: res.data.clientSecret,
    };
  } catch (error) {
    console.error("Error attaching bank account:", error);
    return { success: false };
  }
}

export async function exchangeStripeCodes(code, companyId) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const body = { code };
    if (companyId != null && String(companyId).trim() !== "") {
      body.companyId = String(companyId).trim();
    }

    const response = await axiosInstance.post(
      `${BaseUrl}exchangeStripeCode`,
      body,
      {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching or saving Stripe data", error);
    throw error;
  }
}

/** Stripe Express Connect: returns { url } to open hosted onboarding */
export async function createStripeExpressOnboardingLink(companyId) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const body = {
    returnOrigin: typeof window !== "undefined" ? window.location.origin : "",
  };
  if (companyId != null && String(companyId).trim() !== "") {
    body.companyId = String(companyId).trim();
  }
  const response = await axiosInstance.post(`${BaseUrl}createStripeExpressOnboardingLink`, body, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
  });
  return response.data;
}

/** One-time URL to open Stripe Express Dashboard (update bank, business info) */
export async function createStripeExpressLoginLink(companyId) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const body = {};
  if (companyId != null && String(companyId).trim() !== "") {
    body.companyId = String(companyId).trim();
  }
  const response = await axiosInstance.post(`${BaseUrl}createStripeExpressLoginLink`, body, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
  });
  return response.data;
}

/** Clear soft-disconnect so invoice payments use the saved Express account again */
export async function reconnectStripeExpressAccount(companyId) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const body = {};
  if (companyId != null && String(companyId).trim() !== "") {
    body.companyId = String(companyId).trim();
  }
  const response = await axiosInstance.post(`${BaseUrl}reconnectStripeExpressAccount`, body, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
  });
  return response.data;
}

/** Refresh stripe_connect stripeDetails after Express onboarding */
export async function syncStripeConnectAccount(companyId) {
  const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
  const body = {};
  if (companyId != null && String(companyId).trim() !== "") {
    body.companyId = String(companyId).trim();
  }
  const response = await axiosInstance.post(`${BaseUrl}syncStripeConnectAccount`, body, {
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
  });
  return response.data;
}

export async function verifyAchMicrodeposits(intentId, amounts) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(`${BaseUrl}stripeProxyVerifyMicrodeposits`, { intentId, amounts }, { headers: { ...defaultHeaders, ...authHeaders } });
    return true;
  } catch (error) {
    console.error("Error verifyAchMicrodeposits:", error);
    return false;
  }
}

export async function verifyAchDescriptor(intentId, descriptor_code) {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(`${BaseUrl}stripeProxyVerifyMicrodeposits`, { intentId, descriptorCode: descriptor_code }, { headers: { ...defaultHeaders, ...authHeaders } });
    return true;
  } catch (error) {
    console.error("Error verifyAchDescriptor:", error);
    return false;
  }
}


export async function savePaymentDetailsForVerification(paymentIntentId, verificationType, userId, newPriceId, newAmounts, setUpIntentId = null, subscriptionId = null, newProduct = null) {
  try {
    const paymentDetailsRef = collection(firebase, COLLECTIONS.PAYMENT_VERIFICATION);
    await addDoc(paymentDetailsRef, {
      paymentIntentId,
      setUpIntentId,
      verificationType,
      subscriptionId,
      newProduct,
      userId,
      status: 'pending',
      newPriceId,
      newAmounts,
      method: 'during_verification',
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error saving payment details for verification:", error);
  }
}

/**
 * After a successful repurchase (post-cancel new subscription): save new promo or clear stale one.
 */
export async function finalizeUserPromoCodeAfterRepurchase(
  userId,
  { promoCode = "", promoExplicitlyApplied = false } = {}
) {
  if (!userId) return { success: false };

  try {
    const userDocRef = doc(firebase, COLLECTIONS.USERS, userId);
    const trimmed = typeof promoCode === "string" ? promoCode.trim().toUpperCase() : "";

    if (promoExplicitlyApplied && trimmed) {
      await setDoc(userDocRef, { userPromoCode: trimmed }, { merge: true });
      return { success: true, action: "set", code: trimmed };
    }

    await setDoc(userDocRef, { userPromoCode: deleteField() }, { merge: true });
    return { success: true, action: "cleared" };
  } catch (error) {
    console.error("finalizeUserPromoCodeAfterRepurchase failed:", error);
    return { success: false, error };
  }
}

export async function savePromoCode(code, userId) {
  try {
    if (!code || !userId) return;

    const normalizedCode = String(code).trim().toUpperCase();

    const userDocRef = doc(firebase, COLLECTIONS.USERS, userId);

    const userSnap = await getDoc(userDocRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();

      if (userData?.userPromoCode) {
        console.log("User already has a promo code");
        return;
      }
    }

    await setDoc(
      userDocRef,
      { userPromoCode: normalizedCode },
      { merge: true }
    );

    const promoRef = collection(firebase, "promoCodes");

    const promoQuery = query(
      promoRef,
      where("code", "==", normalizedCode),
    );

    const promoSnapshot = await getDocs(promoQuery);

    if (promoSnapshot.empty) {
      console.log("Promo code not found");
      return;
    }

    const promoDoc = promoSnapshot.docs[0];
    const promoData = promoDoc.data();

    if (promoData.isApplied) {
      console.log("Promo already used");
      return;
    }

    await updateDoc(promoDoc.ref, {
      isApplied: true,
      userId,
      updatedAt: serverTimestamp(),
    });

  } catch (error) {
    console.error("Error saving promocode:", error);
  }
}

export async function updatePaymentVerification(id) {
  try {
    const docRef = doc(firebase, COLLECTIONS.PAYMENT_VERIFICATION, id);
    const data = {
      status: 'verified',
      verifiedAt: serverTimestamp(),
    }
    await updateDoc(docRef, data);

    console.log("Payment verification updated successfully");
  } catch (error) {
    console.error("Error updating payment verification:", error);
  }
}

export async function getPendingPaymentVerificationByUser(userId, methods) {
  try {
    const paymentDetailsRef = collection(firebase, COLLECTIONS.PAYMENT_VERIFICATION);

    const methodList = Array.isArray(methods) ? methods : [methods];

    let response = {};

    for (const method of methodList) {
      const q = query(
        paymentDetailsRef,
        where("userId", "==", userId),
        where("status", "==", "pending"),
        where("method", "==", method)
      );

      const snapshot = await getDocs(q);
      const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      response[method] = results;
    }

    return response;
  } catch (error) {
    console.error("Error fetching pending payment verification by user:", error);
    return {};
  }
}

export async function getAvailablePromoCodes(max = 50) {
  const cached = readSessionCache(PROMO_CODES_SESSION_KEY, 15 * 60 * 1000); // 15 min cache
  if (cached) return cached;

  try {
    const promoRef = collection(firebase, "promoCodes");

    const q = query(
      promoRef,
      where("isActive", "==", true),
      where("isApplied", "==", false),
      limit(max)
    );

    const snapshot = await getDocs(q);

    const promoCodes = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    writeSessionCache(PROMO_CODES_SESSION_KEY, promoCodes);
    return promoCodes;
  } catch (error) {
    console.error("Error fetching promo codes:", error);
    return [];
  }
}

// export async function exportCollection() {
//   const collectionName = COLLECTIONS.SUPPLIES_INDUSTRY;
//   const snapshot = await getDocs(collection(firebase, collectionName));

//   const documents = [];
//   snapshot.forEach(doc => {
//     documents.push({ id: doc.id, ...doc.data() });
//   });

//   const json = JSON.stringify(documents, null, 2);
//   const blob = new Blob([json], { type: 'application/json' });
//   const url = URL.createObjectURL(blob);

//   const a = document.createElement('a');
//   a.href = url;
//   a.download = `${collectionName}-export.json`;
//   a.click();
//   URL.revokeObjectURL(url);
// }

// export async function importCollection() {
//   try {
//     const collectionName = COLLECTIONS.SUPPLIES_CATEGORY;
//     const collectionRef = collection(firebase, collectionName);
    
//     const addPromises = [];

//     for (const item of serviceJson) {
//      const docRef = doc(collectionRef, item.id);
//      const addPromise = setDoc(docRef, item);
//       addPromises.push(addPromise);
//     }

//     await Promise.all(addPromises);
    
//     console.log(`${addPromises.length} documents imported to Firestore using existing IDs!`);
//   } catch (error) {
//     console.error("Error importing data to Firestore:", error);
//   }
// }


// export const downloadSpanishTranslations = async () => {
//   try {
//     const snapshot = await getDocs(
//       collection(firebase, COLLECTIONS.SUPPLIES_CATEGORY)
//     );

//     const translations = {};

//     snapshot.forEach((doc) => {
//       const data = doc.data();

//       const title = data?.title;
//       const spanishTitle = data?.spanish_title;

//       // Only add valid records
//       if (title && spanishTitle) {
//         translations[title] = spanishTitle;
//       }
//     });

//     // Convert to JSON string
//     const jsonString = JSON.stringify(translations, null, 2);

//     // Create downloadable file
//     const blob = new Blob([jsonString], {
//       type: "application/json",
//     });

//     const url = window.URL.createObjectURL(blob);

//     // Auto download
//     const a = document.createElement("a");
//     a.href = url;
//     a.download = "spanish-translations.json";
//     a.style.display = "none";

//     document.body.appendChild(a);
//     a.click();

//     // Cleanup
//     document.body.removeChild(a);
//     window.URL.revokeObjectURL(url);

//     console.log("File downloaded successfully");
//   } catch (error) {
//     console.error("Error downloading translations:", error);
//   }
// };


export async function setAllDataintoDatabase() {
  await updateDoc(doc(firebase, "plansAccess", "UeXvOdK2rEYPywPVDEbW"), {
  "features.account.data_retention": "30 days",
  "limits.storage_gb": 5,
  "features.freemium_display.included": [
    "Scheduling (30 days)",
    "Create & manage issues",
    "Proof of service",
    "Real-time team chat",
    "Download verified photos"
  ],
  "features.freemium_display.also_included": [
    "Contract reminders",
    "Email notifications",
    "Standard team modules",
    "Mobile app (iOS & Android)",
    "Self-help knowledge base"
  ],
  "features.freemium_display.upgrade_to_unlock": [
    "GPS / geofencing",
    "Real-time shift alerts",
    "Push & SMS notifications",
    "Client portal access",
    "Analytics & reports"
  ]
});
}

export async function updateStripeMarketingFeatures() {
  const launchFeatures = [
    "Scheduling + GPS check-in / check-out",
    "Proof-of-service (photo + geo)",
    "Create & manage issues",
    "Internal issue escalation",
    "Client portal access (limited)",
    "Real-time chat + private channels",
    "Email & push notifications",
    "Supplies management (basic)",
    "Standard reports (full)",
    "24-hour support response",
  ];

  const growthFeatures = [
    "Issue escalation to client portal",
    "Public web page for issues",
    "Auto-delivered client reports",
    "Full client portal access",
    "One-touch shift reassignment",
    "Custom dashboard & check-in rules",
    "Periodic task scheduling",
    "Advanced analytics",
    "SMS alerts -- 500/mo included",
    "12-hour support response",
    "Client acknowledgment on proof-of-service",
    "Branded PDF service report",
  ];

  const scaleFeatures = [
    "Predictive analysis",
    "Multi-manager role access",
    "Role-based access by client/site",
    "12-month photo & service history",
    "Clothing & custom supply rules",
    "SMS alerts -- 1,000/mo included",
    "Priority support (3-hour response)",
    "Dedicated onboarding specialist",
  ];

  const updatesByPriceId = {
    // Launch (monthly + yearly)
    "1xTbJdnaX5twYmRzpsht": launchFeatures,

    // Growth (monthly + yearly)
    "YGpNh2tkGK5prs5SiG07": growthFeatures,

    // Scale (monthly + yearly)
    "e2lVRxSS0MCfcxw97t4i": scaleFeatures,
  };

  const updated = [];
  for (const [priceId, marketingFeatures] of Object.entries(updatesByPriceId)) {
    await updateDoc(doc(firebase, 'plansAccess', priceId), {
      "features.planFeatures": marketingFeatures,
    });
    updated.push(priceId);
  }

  return { status: true, updatedCount: updated.length, updatedPriceIds: updated };
}

// export async function updatePlanFeatures(planId) {
//   const isFree = ["freemium_plan"].includes(planId);
//   const isLaunch = planId === "launch_plan" || planId === "standard_plan";
//   const isGrowth = ["growth_plan", "plus_plan"].includes(planId);
//   const isScale = ["scale_plan", "professional_plan", "trial_plan"].includes(planId);

//   // ---------------- FEATURES ----------------
//   const features = {
//     account: {
//       backoffice_seats: isScale ? 5 : isGrowth ? 3 : 1,
//       field_users: isScale ? 50 : isGrowth ? 30 : isLaunch ? 10 : 4,
//       sites: isFree ? 3 : "unlimited",
//       photo_history: isScale ? "12 months" : isGrowth ? "6 months" : isLaunch ? "3 months" : "30 days",
//       storage_gb: isScale ? 200 : isGrowth ? 100 : isLaunch ? 50 : 5
//     },

//     dashboard: {
//       standard_modules: true,
//       custom_modules: isGrowth || isScale,
//       daily_summary: isFree ? "list" : "full",
//       communication_channel: isFree ? "limited" : "full"
//     },

//     appointments: {
//       drag_drop_calendar: true,
//       shift_reassignment: isGrowth || isScale,
//       shift_checklist: true,
//       recurring_appointments: true,
//       real_time_alerts: isFree ? [] : isLaunch ? ["email","push"] : ["email","push","sms"],
//       break_tracking: !isFree,
//       geotagging: !isFree,
//       geofencing: isGrowth || isScale,
//       cancel_reschedule: true,
//       punch_tracking: true,
//       task_tracking: isGrowth || isScale,
//       business_hours: !isFree,
//       scheduling_rules: !isFree,
//       checkin_rules: isGrowth || isScale
//     },

//     employee_manager: {
//       invite: true,
//       roles: true,
//       manage: true,
//       team_leads: isGrowth || isScale
//     },

//     client_manager: {
//       notes: true,
//       roles: !isFree,
//       contracts: isFree ? 3 : "unlimited",
//       reminders: true,
//       proof_rules: isGrowth || isScale,
//       client_portal: isFree ? "none" : isLaunch ? "limited" : "full",
//       site_shifts: true,
//       tasks: true,
//       periodic_tasks: isGrowth || isScale,
//       auto_reports: isGrowth || isScale
//     },

//     communications: {
//       chat: true,
//       channels: !isFree,
//       email: true,
//       push: !isFree,
//       sms: isGrowth || isScale,
//       scheduled_notifications: isScale ? 1000 : isGrowth ? 500 : 0
//     },

//     proof_of_service: {
//       company_performance: true,
//       employee_performance: !isFree,
//       client_metrics: isGrowth || isScale,
//       photos_download: true,
//       analytics: isScale ? "predictive" : isGrowth ? "advanced" : isLaunch ? "simple" : "none"
//     },

//     issue_manager: {
//       create: true,
//       internal_escalation: !isFree,
//       client_escalation: isGrowth || isScale,
//       public_page: isGrowth || isScale,
//       audit_history: isScale ? "12 months" : isGrowth ? "6 months" : isLaunch ? "3 months" : "30 days",
//       photo_upload: true,
//       archive_duration: isScale ? "12 months" : isGrowth ? "6 months" : isLaunch ? "3 months" : "30 days"
//     },

//     supply_manager: {
//       requests: !isFree,
//       predefined_list: isGrowth || isScale,
//       custom_supplies: !isFree,
//       real_time_status: isGrowth || isScale,
//       clothing_rules: isGrowth || isScale,
//       archive: isGrowth || isScale
//     },

//     reports: {
//       standard: isFree ? "limited" : "full",
//       advanced: isGrowth || isScale ? "partial" : "none"
//     },

//     support: {
//       guided_tours: true,
//       response_time: isScale ? "3 hours" : isGrowth ? "12 hours" : isLaunch ? "24 hours" : "self",
//       knowledge_base: true
//     },

//     mobile: {
//       dashboard: true,
//       attention_center: !isFree,
//       routing: true,
//       task_details: true,
//       request_supplies: !isFree,
//       appointment_range: isScale ? "12 months" : isGrowth ? "6 months" : isLaunch ? "3 months" : "30 days",
//       create_issues: true,
//       chat: true,
//       notifications: isFree ? ["email"] : isLaunch ? ["email","push"] : ["email","push","sms"],
//       support: isScale ? "3 hours" : isGrowth ? "12 hours" : isLaunch ? "24 hours" : "self",
//       language: true,
//       theme: !isFree
//     }
//   };

//   // ---------------- WEB ACCESS ----------------
//   const webPortalAccess = normalizePlan({
//     features,
//     webPortalAccess: deriveWebPortalAccessFromFeatures(features),
//   }).webPortalAccess;
//   const q = query(
//     collection(firebase, "plansAccess"),
//     where("planId", "==", planId)
//   );

//   const snap = await getDocs(q);

//   if (!snap.empty) {
//     const docRef = snap.docs[0].ref;

//     await setDoc(
//       docRef,
//       { features, webPortalAccess},
//       { merge: true }
//     );
//   }
//   console.log(`✅ Plan ${planId} updated`);
// }

// const MASTER_FEATURES = [
//   'dashboard', 'appointments', 'clients', 'invoices', 'employees', 'reports', 
//   'advanced_reports', 'live_chat', 'message_board', 'sms_notifications', 
//   'push_notifications', 'supplies', 'issues', 'client_portal', 'contract_reminders', 
//   'proof_of_service', 'internal_reviews', 'notifications', 'google_reviews', 
//   'api_integrations', 'mobile_app'
// ];
// const featureMap = {
//   DASHBOARD: 'dashboard',
//   APPOINTMENTS: 'appointments', CRT_APPT: 'appointments', MNG_APPT: 'appointments',
//   CLIENTS: 'clients', CLIENT_SUMMARY: 'clients',
//   INVOICE: 'invoices', CRT_INV: 'invoices', MNG_INV: 'invoices',
//   EMPLOYEE: 'employees', MANAGE_GROUPS: 'employees',
//   STANDARD_REPORTS: 'reports', REPORTS: 'reports',
//   ADVANCED_REPORTS: 'advanced_reports', ADV_CLIENT_REPORTS: 'advanced_reports',
//   LIVE_CHAT: 'live_chat', CHAT_MEDIA: 'live_chat',
//   MESSAGE_BOARD: 'message_board',
//   SMS_NOTIFICATION: 'sms_notifications',
//   PUSH: 'push_notifications',
//   SUPPLIES: 'supplies', MNG_SUPPLIES: 'supplies',
//   ISSUES: 'issues', MNG_ISSUES: 'issues',
//   CLIENT_PORTAL: 'client_portal',
//   CONTRACT_REMINDERS: 'contract_reminders',
//   PROOF_OF_SERVICE: 'proof_of_service', POS_WORK: 'proof_of_service',
//   INTERNAL_REVIEWS: 'internal_reviews',
//   NOTIFICATION: 'notifications'
// };
// const BASE_PERMS = ["DASHBOARD", "APPOINTMENTS", "CLIENTS", "INVOICE", "EMPLOYEE", "STANDARD_REPORTS", "MESSAGE_BOARD", "LIVE_CHAT", "SUPPLIES", "ISSUES", "NOTIFICATION", "CONTRACT_REMINDERS", "PROOF_OF_SERVICE", "INTERNAL_REVIEWS"];
// const PLUS_PERMS = [...BASE_PERMS, "SMS_NOTIFICATION", "PUSH"];
// const GROWTH_PERMS = [...PLUS_PERMS, "ADVANCED_REPORTS", "CLIENT_PORTAL"];
// const PLANS = [
//   { id: "freemium_plan", name: "Freemium Plan", stripeProductId: "prod_UKkbABCjHCyXeg", price: { monthly: 0 }, limits: { max_users: 5, backoffice_users: 1, active_job_sites: 3 }, permissions: ["DASHBOARD", "APPOINTMENTS", "CLIENTS", "EMPLOYEE", "MESSAGE_BOARD", "LIVE_CHAT", "ISSUES", "PUSH", "CONTRACT_REMINDERS", "PROOF_OF_SERVICE", "INTERNAL_REVIEWS"] },
//   { id: "trial_plan", name: "Trial Plan", stripeProductId: "prod_SoFkXIV3TK8yY1", price: { monthly: 0 }, limits: { max_users: 5, backoffice_users: 1, storage_gb: 50 }, permissions: BASE_PERMS },
//   { id: "standard_plan", name: "Standard Plan", stripeProductId: "prod_SoFmdavyFm07un", price: { monthly: 99 }, limits: { max_users: 5, backoffice_users: 3, storage_gb: 50 }, permissions: BASE_PERMS },
//   { id: "launch_plan", name: "Launch Plan", stripeProductId: "prod_U55YpGKZ7825uv", price: { monthly: 99 }, limits: { max_users: 10, backoffice_users: 1, storage_gb: 50 }, permissions: BASE_PERMS },
//   { id: "plus_plan", name: "Plus Plan", stripeProductId: "prod_SoFvvpamskLVDJ", price: { monthly: 199 }, limits: { max_users: 15, backoffice_users: 3, storage_gb: 100 }, permissions: PLUS_PERMS },
//   { id: "growth_plan", name: "Growth Plan", stripeProductId: "prod_U55bwiAZVDGhNT", price: { monthly: 249 }, limits: { max_users: 30, backoffice_users: 3, storage_gb: 100 }, permissions: GROWTH_PERMS },
//   { id: "professional_plan", name: "Professional Plan", stripeProductId: "prod_SoG1sJnI8DKM6A", price: { monthly: 299 }, limits: { max_users: 25, backoffice_users: 5, storage_gb: 200 }, allFeaturesEnabled: true },
//   { id: "scale_plan", name: "Scale Plan", stripeProductId: "prod_U55dRq6GVSUbKS", price: { monthly: 449 }, limits: { max_users: 50, backoffice_users: 5, storage_gb: 200 }, allFeaturesEnabled: true }
// ];
// export async function migratePlansToFirestore() {
//   console.log('🚀 Starting Idempotent Plan Migration...');
//   const plansCollection = collection(firebase, 'plansAccess');
//   for (const planData of PLANS) {
//     try {
//       const { id, permissions, allFeaturesEnabled, ...rest } = planData;
//       // 1. Check if plan already exists using a query on planId
//       const q = query(plansCollection, where("planId", "==", id));
//       const querySnapshot = await getDocs(q);
      
//       if (!querySnapshot.empty) {
//         console.log(`⏩ Plan "${planData.name}" already exists in Firestore. skipping...`);
//         continue; // Skip to next plan
//       }
//       const features = {};
//       // 2. Initialize Features
//       MASTER_FEATURES.forEach(key => {
//         features[key] = allFeaturesEnabled ? true : false;
//       });
//       // 3. Map permission flags
//       if (permissions && !allFeaturesEnabled) {
//         permissions.forEach(perm => {
//           const key = featureMap[perm];
//           if (key) features[key] = true;
//         });
//       }
//       // Feature override
//       features.mobile_app = true; 
//       const docData = {
//         ...rest,
//         planId: id,
//         features,
//         isActive: true,
//         isProductionReady: true,
//         createdAt: serverTimestamp()
//       };
//       // 4. Add unique document
//       const docRef = await addDoc(plansCollection, docData);
//       console.log(`✅ Plan "${planData.name}" added successfully. Auto-ID: ${docRef.id}`);
//     } catch (e) {
//       console.error(`❌ Migration error for ${planData.name}:`, e.message);
//     }
//   }
//   console.log('✨ Migration process finished.');
// }
// /**
//  * Seed dummy communication logs for testing:
//  * - Delivery Issues report (failed sms/push)
//  * - Client Notification Log report (sent/delivered email/sms)
//  *
//  * NOTE:
//  * This uses the secured Cloud Function endpoint so it bypasses client Firestore-rule denials.
//  */
// export async function seedClientCommunicationsData({
//   companyId = "19iGto2FX8ZKVZ91o7jgegAFJz33",
//   clientId = "MWAsbZBA5ofygWMk1peV",
//   siteId = "MWAsbZBA5ofygWMk1peV",
//   sentBy = "19iGto2FX8ZKVZ91o7jgegAFJz33",
//   clientName = "Test",
//   count = 20,
// } = {}) {
//   if (!companyId || !clientId) {
//     throw new Error("companyId and clientId are required");
//   }

//   const endpoint = `${BaseUrl}logClientCommunicationEvent`;
//   const total = Math.max(2, Math.min(Number(count) || 12, 100));
//   const failedCount = Math.floor(total / 2);
//   const successCount = total - failedCount;

//   const typeOptions = ["issue_escalation", "review_request", "proof_of_service"];
//   const failedReasonOptions = [
//     "Invalid phone number",
//     "Recipient unavailable",
//     "Device token not registered",
//     "Message blocked by carrier",
//     "Provider timeout",
//   ];

//   const records = [];
//   const now = Date.now();

//   // Failed rows -> Delivery Issues report
//   for (let i = 0; i < failedCount; i++) {
//     const isSms = i % 2 === 0;
//     const channel = isSms ? "sms" : "push";
//     records.push({
//       companyId: String(companyId),
//       clientId: String(clientId),
//       siteId: String(siteId || clientId),
//       type: typeOptions[i % typeOptions.length],
//       sentBy: String(sentBy),
//       recipientName: String(clientName),
//       recipientContact: isSms
//         ? `+1555000${String(100 + i)}`
//         : `${String(clientName).toLowerCase().replace(/\s+/g, ".")}@example.com`,
//       channel,
//       status: "failed",
//       providerMessageId: `${channel}_seed_${now}_${i}`,
//       failureReason: failedReasonOptions[i % failedReasonOptions.length],
//       referenceId: `seed_failed_${now}_${i}`,
//       deliveryPayload: isSms
//         ? {
//             phoneNumber: [`+1555000${String(100 + i)}`],
//             textMessage: "Seed test SMS for Delivery Issues report",
//           }
//         : {
//             userIds: [`seedUser_${i}`],
//             title: "Seed Push Notification",
//             textMessage: "Seed test push for Delivery Issues report",
//             extData: { type: "Messages", id: `seed_ext_${i}` },
//           },
//     });
//   }

//   // Sent/delivered rows -> Client Notification Log report
//   for (let i = 0; i < successCount; i++) {
//     const channel = i % 2 === 0 ? "email" : "sms";
//     records.push({
//       companyId: String(companyId),
//       clientId: String(clientId),
//       siteId: String(siteId || clientId),
//       type: typeOptions[(i + 1) % typeOptions.length],
//       sentBy: String(sentBy),
//       recipientName: String(clientName),
//       recipientContact:
//         channel === "email"
//           ? `${String(clientName).toLowerCase().replace(/\s+/g, ".")}@example.com`
//           : `+1555111${String(200 + i)}`,
//       channel,
//       status: i % 3 === 0 ? "delivered" : "sent",
//       providerMessageId: `${channel}_seed_${now}_${i + 100}`,
//       failureReason: null,
//       referenceId: `seed_success_${now}_${i}`,
//       deliveryPayload:
//         channel === "sms"
//           ? {
//               phoneNumber: [`+1555111${String(200 + i)}`],
//               textMessage: "Seed test SMS for Client Notification Log",
//             }
//           : null,
//     });
//   }

//   const results = await Promise.allSettled(
//     records.map((payload) =>
//       axiosInstance.post(endpoint, payload, {
//         headers: {
//           ...defaultHeaders,
//           Authorization: `Bearer ${getLoginToken()}`,
//         },
//       })
//     )
//   );

//   const fulfilled = results
//     .filter((item) => item.status === "fulfilled")
//     .map((item) => item.value);
//   const failed = results.filter((item) => item.status === "rejected");

//   const ids = fulfilled
//     .map((res) => res?.data?.id)
//     .filter(Boolean);

//   return {
//     status: failed.length === 0,
//     totalCreated: ids.length,
//     failedRows: failedCount,
//     successRows: successCount,
//     totalRequested: records.length,
//     totalFailedRequests: failed.length,
//     ids,
//   };
// }

// Manully update subscription 
 // step 1: Add credit in customer 
 // step 2: set the id and prod id here and run this it work

 
// export async function updatedSubscription() {
//   await stripe.subscriptionSchedules.release("sub_sched_1RyUWMHPX5Uwv3eVVZHW8rNG");

//   const subscription = await stripe.subscriptions.retrieve("sub_1RtRlHHPX5Uwv3eVi72ASEyZ");

//   const updated = await stripe.subscriptions.update("sub_1RtRlHHPX5Uwv3eVi72ASEyZ", {
//     cancel_at_period_end: false,
//     trial_end: "now",
//     items: [
//       {
//         id: subscription.items.data[0].id,
//         price: "price_1RsdVaHPX5Uwv3eVGScEMH75",
//       },
//     ],
//     promotion_code: "promo_1RyPzlHPX5Uwv3eVpXhjT2Jd",
//   });

//   console.log("✅ Updated subscription:", updated);
//   return updated;
// }

// const pricesArray = [

// ];

// export async function storeStripePrices() {
//   const paymentDetailsRef = collection(firebase, COLLECTIONS.STRIPE_PRODUCTS);
//   const batch = writeBatch(firebase);

//   pricesArray.forEach(price => {
//     if (!price?.id) return;

//     const docRef = doc(paymentDetailsRef, price.id);
//       batch.set(docRef, price, { merge: true });
//   });

//   try {
//     await batch.commit();
//     console.log(`All ${pricesArray.length} Stripe prices stored successfully!`);
//   } catch (error) {
//     console.error("Failed to store Stripe prices:", error);
//   }
// }

// const demoPhotos = [
//   "https://firebasestorage.googleapis.com/v0/b/proteams-stage.firebasestorage.app/o/bAsJsT6VPpZqVv9UrhT7vmNF9Rp2%2Fappointment%2Fg9AeFipZBxsLd9j66e9O%2F1770252381378_proof_1770252380906.jpeg?alt=media&token=a7fdd66c-0db0-47cc-90c7-fe03cf5a897c",
//   "https://firebasestorage.googleapis.com/v0/b/proteams-stage.firebasestorage.app/o/bAsJsT6VPpZqVv9UrhT7vmNF9Rp2%2Fappointment%2Fg9AeFipZBxsLd9j66e9O%2F1770252340660_proof_1770252334380.jpeg?alt=media&token=87afe3b0-7473-4017-b9d4-ca3240a1ee51",
//   "https://firebasestorage.googleapis.com/v0/b/proteams-stage.firebasestorage.app/o/bAsJsT6VPpZqVv9UrhT7vmNF9Rp2%2Fappointment%2Fg9AeFipZBxsLd9j66e9O%2F1770252381378_proof_1770252380906.jpeg?alt=media&token=a7fdd66c-0db0-47cc-90c7-fe03cf5a897c",
//   "https://firebasestorage.googleapis.com/v0/b/proteams-stage.firebasestorage.app/o/bAsJsT6VPpZqVv9UrhT7vmNF9Rp2%2Fappointment%2Fg9AeFipZBxsLd9j66e9O%2F1770252381378_proof_1770252380906.jpeg?alt=media&token=a7fdd66c-0db0-47cc-90c7-fe03cf5a897c",
//   "https://firebasestorage.googleapis.com/v0/b/proteams-stage.firebasestorage.app/o/bAsJsT6VPpZqVv9UrhT7vmNF9Rp2%2Fappointment%2Fg9AeFipZBxsLd9j66e9O%2F1770252340660_proof_1770252334380.jpeg?alt=media&token=87afe3b0-7473-4017-b9d4-ca3240a1ee51",
// ];

// const LATE_COUNT = 40;
// const MISSING_COUNT = 40;
// const DAYS_BACK = 25;

// const randomBetween = (min, max) =>
//   Math.random() * (max - min) + min;

// const randomPastDate = (daysBack = DAYS_BACK) => {
//   const now = new Date();
//   const past = new Date(now);
//   past.setDate(now.getDate() - Math.floor(Math.random() * daysBack));
//   return past;
// };

// const generateNearbyGPS = (lat, lng) => ({
//   lat: lat + randomBetween(-0.002, 0.002),
//   lng: lng + randomBetween(-0.002, 0.002),
// });

// const randomTimeBetween = (start, end) =>
//   new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));

// const randomLateTime = (end) =>
//   new Date(end.getTime() + randomBetween(30 * 60 * 1000, 60 * 60 * 1000));

// const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);

// export const generateDemoPOSData = async () => {
//   try {
//     console.log("Starting Proof Of Service demo data...");

//     const today = new Date();
//     today.setHours(23, 59, 59, 999);

//     const past30 = new Date();
//     past30.setDate(today.getDate() - DAYS_BACK);

//     const q = query(
//       collection(firebase, COLLECTIONS.TIMESHEETS),
//       where("companyId", "==", "bAsJsT6VPpZqVv9UrhT7vmNF9Rp2"),
//       where("appointmentDateTime", ">=", past30.toISOString()),
//       where("appointmentDateTime", "<=", today.toISOString()),
//       orderBy("appointmentDateTime", "desc")
//     );

//     const snapshot = await getDocs(q);

//     let docs = snapshot.docs;
//     docs = shuffleArray(docs);

//     const total = docs.length;

//     console.log("Total appointments found:", total);

//     // Build global type distribution
//     const types = [
//       ...Array(LATE_COUNT).fill("late"),
//       ...Array(MISSING_COUNT).fill("missing"),
//       ...Array(Math.max(0, total - LATE_COUNT - MISSING_COUNT)).fill("verified"),
//     ];

//     shuffleArray(types);

//     let updated = 0;

//     for (let i = 0; i < docs.length; i++) {
//       const appointment = docs[i];
//       const data = appointment.data();
//       const type = types[i];

//       const appointmentDate = randomPastDate();

//       const startTime = new Date(appointmentDate);
//       startTime.setHours(9, 0, 0);

//       const endTime = new Date(appointmentDate);
//       endTime.setHours(17, 0, 0);

//       const updatedTasks = { ...(data.tasks || {}) };

//       const baseLat = data?.location?.lat ?? 40.7128;
//       const baseLng = data?.location?.lng ?? -74.006;

//       Object.keys(updatedTasks).forEach((shiftKey) => {
//         const shift = updatedTasks[shiftKey];
//         if (!shift?.tasksList) return;

//         shift.tasksList = shift.tasksList.map((task) => {
//           let proofs = [];

//           if (type === "verified") {
//             const capturedAt = randomTimeBetween(startTime, endTime);
//             const photo = demoPhotos[Math.floor(Math.random() * demoPhotos.length)];

//             proofs.push({
//               captured_at_device: Timestamp.fromDate(capturedAt),
//               gps: generateNearbyGPS(baseLat, baseLng),
//               verificationSource: "CLIENT_ADDRESS_GEOCODED",
//               uploaded_at: Timestamp.now(),
//               source: "camera",
//               url: photo,
//               verified: true,
//               flagged: false,
//               taskId: task?.taskId || task?.id || "",
//               appointmentId: appointment.id,
//               distanceInMeters: Math.floor(Math.random() * 40),
//             });
//           }

//           if (type === "late") {
//             const capturedAt = randomLateTime(endTime);
//             const photo = demoPhotos[Math.floor(Math.random() * demoPhotos.length)];

//             proofs.push({
//               captured_at_device: Timestamp.fromDate(capturedAt),
//               gps: generateNearbyGPS(baseLat, baseLng),
//               verificationSource: "CLIENT_ADDRESS_GEOCODED",
//               uploaded_at: Timestamp.now(),
//               source: "camera",
//               url: photo,
//               verified: false,
//               flagged: false,
//               taskId: task?.taskId || task?.id || "",
//               appointmentId: appointment.id,
//               distanceInMeters: Math.floor(Math.random() * 40),
//             });
//           }

//           return {
//             ...task,
//             pos: true,
//             proofs: type === "missing" ? [] : proofs,
//             completed: true,
//           };
//         });

//         updatedTasks[shiftKey] = shift;
//       });

//       await updateDoc(
//         doc(firebase, COLLECTIONS.TIMESHEETS, appointment.id),
//         {
//           completed: true,
//           tasks: updatedTasks,
//           isTestData: true,
//           lastUpdatedAt: Timestamp.now(),
//         }
//       );

//       updated++;
//     }

//     console.log("Finished updating demo data");
//     console.log({
//       totalAppointments: total,
//       late: LATE_COUNT,
//       missing: MISSING_COUNT,
//       verified: total - LATE_COUNT - MISSING_COUNT,
//     });

//   } catch (error) {
//     console.error("Demo data generation failed:", error);
//   }
// };